import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCurrentTrips } from "../hooks/useCurrentTrips";
import { CurrentTripCard } from "../components/home/CurrentTripCard";
import type { TripWithId } from "../hooks/useTrips";
import { formatSegmentDateRange, tripStatusLabel } from "../utils/tripDisplay";
import { usePageBar } from "../contexts/PageBarContext";
import "./Home.css";

function UpcomingTripCard({ trip }: { trip: TripWithId }) {
  const status = tripStatusLabel(trip.startDate, trip.endDate);
  return (
    <Link to={`/trips/${trip.id}`} className="home-upcoming-card">
      <span className="home-card-eyebrow">Up next</span>
      <h2>{trip.name}</h2>
      <div className="home-upcoming-meta">
        <span>{formatSegmentDateRange(trip.startDate, trip.endDate)}</span>
        {status.label && (
          <span className={`ctc-status ctc-status-${status.tone}`}>{status.label}</span>
        )}
      </div>
    </Link>
  );
}

export function Home() {
  const { user } = useAuth();
  const { currentTrips, focusedTripId, setFocusedTrip, upcomingTrip, allTrips, loading } =
    useCurrentTrips();
  usePageBar({ title: "Today" });

  const firstName = user?.displayName?.trim().split(/\s+/)[0] || "Traveler";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const greetingSub =
    currentTrips.length > 0
      ? "Here's what's happening."
      : upcomingTrip
        ? "Your next trip is on the horizon."
        : "Ready when you are.";

  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  return (
    <div className="page home-page">
      {/* During a trip the trip itself is the headline; the greeting is for the days between. */}
      {currentTrips.length === 0 && (
        <header className="home-greeting">
          <h1>
            {greeting}, {firstName} 👋
          </h1>
          <p className="home-greeting-sub">{greetingSub}</p>
        </header>
      )}

      {currentTrips.length > 0 ? (
        <div className="home-trips">
          {currentTrips.length > 1 && (
            <p className="home-hint">{currentTrips.length} trips happening right now</p>
          )}
          {currentTrips.map((trip) => (
            <CurrentTripCard
              key={trip.id}
              trip={trip}
              focused={trip.id === focusedTripId}
              showFocusToggle={currentTrips.length > 1}
              onFocus={() => setFocusedTrip(trip.id)}
            />
          ))}
        </div>
      ) : upcomingTrip ? (
        <UpcomingTripCard trip={upcomingTrip} />
      ) : allTrips.length > 0 ? (
        <div className="home-empty-card">
          <h2>No trip in progress</h2>
          <p className="text-muted">
            Nothing's happening right now. Revisit a past trip or start a new one.
          </p>
        </div>
      ) : (
        <div className="home-empty-card">
          <h2>Plan your first trip ✈️</h2>
          <p className="text-muted">
            Create a trip to start tracking segments and logging activities.
          </p>
          <Link to="/trips/new" className="btn">
            + New trip
          </Link>
        </div>
      )}

      {allTrips.length > 0 && (
        <div className="home-footer-actions">
          <Link to="/trips" className="btn btn-secondary">
            All trips ({allTrips.length})
          </Link>
          <Link to="/trips/new" className="btn">
            + New trip
          </Link>
        </div>
      )}
    </div>
  );
}
